import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import CanvasJSReact from "@canvasjs/react-charts";
import { IndianRupee, Layers, Percent, Receipt } from "lucide-react";
import { getPaymentType } from "../../Action/courseAction";
import {
  CustomAdminTable,
  CustomButton,
  CustomDropdowns,
  CustomPageLayout,
  CustomStatCard,
  useHeaderFilterButton,
} from "../../Component/customComponents";
import {
  TableTitle,
  TotalsModal,
  buildDateFilterFields,
  formatINR,
  todayISO,
} from "./transactionCommon";
import {
  EDUTECH,
  PUBLICATION,
  SHOPIFY,
  TRANSACTION_TYPES,
  buildRevenueChartOptions,
  contributionPercent,
  defaultPaymentFor,
  headlineRevenue,
  paymentTypesFor,
  revenueBreakdownRows,
  revenueTotals,
  rowsForType,
} from "./revenueAnalysisCommon";

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

// read "today" when the page opens
const emptyFilters = () => ({ startDate: todayISO(), endDate: todayISO(), paymentType: "" });

/** Request body for the analysis API (same keys as before) */
const buildReqBody = (f) => ({
  startDate: f.startDate,
  endDate: f.endDate,
  ...(f.paymentType && { paymentType: f.paymentType._id }),
});

/**
 * Revenue analysis page: one row per group (source, state, …) for the chosen transaction type.
 *
 * Props:
 *  - pageTitle        e.g. "SOURCE WISE ANALYSIS"
 *  - chartTitle       e.g. "Source Wise Analysis"
 *  - groupLabel       column + chart axis name, e.g. "Source" / "State"
 *  - fetchAction      (body, callback) => thunk; callback gets the rows ([] on failure)
 *  - skipZeroInChart  leave groups with no revenue out of the chart
 *  - mapRows(response, typeId)        optional: response → table rows (default: rowsForType)
 *  - paymentOptionsFor(all, typeId)   optional: payment types offered (default: paymentTypesFor)
 *  - tableId
 */
const RevenueAnalysisPage = ({
  pageTitle,
  chartTitle,
  groupLabel,
  fetchAction,
  skipZeroInChart = false,
  mapRows = (resp, typeId) => rowsForType(Array.isArray(resp) ? resp : [], typeId),
  paymentOptionsFor = paymentTypesFor,
  tableId,
}) => {
  const dispatch = useDispatch();
  const headerFilter = useHeaderFilterButton(); // header Filters button → the table's drawer

  const [filters, setFilters] = useState(emptyFilters);
  const [transactionType, setTransactionType] = useState(TRANSACTION_TYPES[0]); // Edutech, as before
  const [allPaymentTypes, setAllPaymentTypes] = useState([]);

  const [apiRows, setApiRows] = useState([]); // raw response, split per type on the fly
  const [loading, setLoading] = useState(false);
  const [totalsOpen, setTotalsOpen] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const typeId = transactionType?._id;

  /* ---------- API (the analysis actions call back [] on failure) ---------- */
  const fetchAnalysis = (f) => {
    setLoading(true);
    dispatch(
      fetchAction(buildReqBody(f), (resp) => {
        setApiRows(resp ?? []); // shaped by mapRows below
        setPage(1);
        setLoading(false);
      }),
    );
  };

  useEffect(() => {
    fetchAnalysis(filters); // today, Edutech — as before
    dispatch(getPaymentType((res) => setAllPaymentTypes(res || [])));
  }, []);

  /* ---------- transaction type (toolbar) ---------- */
  const onChangeType = (option) => {
    if (!option || option._id === typeId) return;
    setTransactionType(option);
    // payment types differ per type (Shopify has exactly one) → reset and reload
    const next = { ...filters, paymentType: defaultPaymentFor(allPaymentTypes, option._id) };
    setFilters(next);
    setSearchText("");
    fetchAnalysis(next);
  };

  /* ---------- filter drawer ---------- */
  const paymentOptions = useMemo(() => paymentOptionsFor(allPaymentTypes, typeId), [paymentOptionsFor, allPaymentTypes, typeId]);

  const filterFields = useMemo(
    () => [
      ...buildDateFilterFields(),
      {
        key: "paymentType",
        label: "Payment Type",
        type: "dropdown",
        options: paymentOptions,
        search: false,
        defaultValue: () => defaultPaymentFor(allPaymentTypes, typeId),
        disabled: typeId === SHOPIFY, // only one Shopify payment type
        hint: typeId === SHOPIFY ? "Shopify has a single payment type" : undefined,
      },
    ],
    [paymentOptions, allPaymentTypes, typeId],
  );

  /* ---------- data for the selected type ---------- */
  const rows = useMemo(() => mapRows(apiRows, typeId), [mapRows, apiRows, typeId]);
  const totals = useMemo(() => revenueTotals(rows), [rows]);
  const headline = headlineRevenue(totals, typeId);
  const chartOptions = useMemo(
    () => buildRevenueChartOptions({ rows, typeId, title: chartTitle, axisXTitle: groupLabel, skipZero: skipZeroInChart }),
    [rows, typeId, chartTitle, groupLabel, skipZeroInChart],
  );

  const filteredRows = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => String(row?._id || "Unknown").toLowerCase().includes(term));
  }, [rows, searchText]);

  const pagedRows = useMemo(
    () => filteredRows.slice((page - 1) * pageSize, page * pageSize),
    [filteredRows, page, pageSize],
  );

  const money = (v) => <span style={{ fontVariantNumeric: "tabular-nums" }}>{formatINR(v)}</span>;

  const columns = [
    { key: "_id", header: groupLabel, minWidth: 160, render: (v) => <strong>{v || "Unknown"}</strong> },
    { key: "publication", header: "Publication Revenue", align: "right", hidden: typeId !== PUBLICATION, render: money },
    { key: "shopifyRevenue", header: "Shopify Revenue", align: "right", hidden: typeId !== SHOPIFY, render: money },
    { key: "actualTotalAmount", header: "Edutech Actual Revenue", align: "right", hidden: typeId !== EDUTECH, render: money },
    { key: "totalGstAmount", header: "GST Amount", align: "right", hidden: typeId !== EDUTECH, render: money },
    { key: "totalAmount", header: "Total Revenue", align: "right", render: money },
    {
      key: "contribution",
      header: "Overall Contribution",
      minWidth: 160,
      render: (_, row) => {
        const pct = contributionPercent(row, typeId, headline);
        return (
          <div>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{pct.toFixed(2)} %</span>
            <div className="progress mt-1" style={{ height: 6 }} aria-hidden="true">
              <div
                className="progress-bar"
                style={{ width: `${Math.min(100, pct)}%`, background: "var(--cui-primary)" }}
              />
            </div>
          </div>
        );
      },
    },
  ];

  const typeName = transactionType?.name || "Edutech";

  return (
    <CustomPageLayout title={pageTitle} headerActions={headerFilter.button}>
      <div className="d-flex flex-column gap-3">
        {/* ---------- summary ---------- */}
        <div className="row g-3">
          <CustomStatCard
            colClass="col-lg-3 col-sm-6 col-12"
            title="Total Revenue"
            value={formatINR(headline)}
            subtitle={typeId === EDUTECH ? "Edutech actual, excluding GST" : `${typeName} revenue`}
            icon={<IndianRupee size={18} />}
            tone="primary"
            loading={loading}
            onClick={() => setTotalsOpen(true)}
          />
          {typeId === EDUTECH && (
            <CustomStatCard
              colClass="col-lg-3 col-sm-6 col-12"
              title="GST Amount"
              value={formatINR(totals.gst)}
              subtitle="On Edutech revenue"
              icon={<Percent size={18} />}
              tone="warning"
              loading={loading}
            />
          )}
          <CustomStatCard
            colClass="col-lg-3 col-sm-6 col-12"
            title="Total Price"
            value={formatINR(totals.total)}
            subtitle={typeId === EDUTECH ? "Including GST" : "All sources"}
            icon={<Receipt size={18} />}
            tone="success"
            loading={loading}
          />
          <CustomStatCard
            colClass="col-lg-3 col-sm-6 col-12"
            title={`${groupLabel}s`}
            value={rows.length}
            subtitle={rows[0] ? `Top: ${rows[0]._id || "Unknown"}` : "No data"}
            icon={<Layers size={18} />}
            tone="info"
            loading={loading}
          />
        </div>

        {/* ---------- chart (same CanvasJS column chart as before) ---------- */}
        <section className="bg-white border rounded-3 shadow-sm p-3">
          {rows.length > 0 ? (
            <CanvasJSChart options={chartOptions} />
          ) : (
            <div className="text-center text-muted py-5">
              {loading ? "Loading chart…" : "No revenue for this period."}
            </div>
          )}
        </section>

        {/* ---------- table ---------- */}
        <CustomAdminTable
          {...headerFilter.tableProps}
          id={tableId}
          title={
            <TableTitle title={`${typeName} by ${groupLabel} (${rows.length})`} from={filters.startDate} to={filters.endDate} />
          }
          columns={columns}
          data={pagedRows}
          loading={loading}
          rowKey={(row, index) => `${row?._id || "unknown"}-${index}`}
          enableSerialNumber
          serialHeader="S.No"
          search={{
            value: searchText,
            onChange: (v) => {
              setSearchText(v);
              setPage(1);
            },
            placeholder: `Search ${groupLabel.toLowerCase()}...`,
          }}
          toolbarActions={
            <>
              <div style={{ minWidth: 170 }}>
                <CustomDropdowns
                  placeholder="Transaction Type"
                  options={TRANSACTION_TYPES}
                  value={transactionType}
                  onChange={onChangeType}
                  search={false}
                  clearable={false}
                />
              </div>
              <CustomButton
                variant="secondary"
                icon={<Receipt size={15} />}
                onClick={() => setTotalsOpen(true)}
                title="View GST breakdown"
              >
                Total: {formatINR(headline)}
              </CustomButton>
            </>
          }
          advancedFilters={filterFields}
          advancedFilterValues={filters}
          onApplyFilters={(values) => {
            setFilters(values);
            fetchAnalysis(values);
          }}
          applyFiltersOnReset
          filterSubtitle="Pick the date range and payment type"
          pagination={{
            page,
            pageSize,
            total: filteredRows.length,
            onPageChange: setPage,
            onPageSizeChange: (size) => {
              setPageSize(size);
              setPage(1);
            },
          }}
          emptyTitle={searchText ? `No matching ${groupLabel.toLowerCase()}s` : "No revenue"}
          emptyDescription={searchText ? "Try a different search." : "No revenue for this period."}
          maxHeight="70vh"
        />
      </div>

      <TotalsModal
        open={totalsOpen}
        onClose={() => setTotalsOpen(false)}
        subtitle={`${typeName} · ${groupLabel.toLowerCase()} wise`}
        rows={revenueBreakdownRows(totals, typeId)}
      />
    </CustomPageLayout>
  );
};

export default RevenueAnalysisPage;
